/**
 * Reads the roles the backend put into the `roles` claim of a signed access token (JWT).
 *
 * The signature is deliberately not verified. The token arrives straight from our backend over
 * HTTPS, and verifying it in the browser would not stop a script already running on the page. The
 * roles only decide what the UI offers — the backend checks every permission again on each request.
 *
 * Anything malformed yields no roles instead of an error, so a surprising token can at most hide a
 * button; it never breaks the request or the renewal that carried it.
 */
const TOKEN_SEGMENT_SEPARATOR = '.';
const SIGNED_TOKEN_SEGMENT_COUNT = 3;
const PAYLOAD_SEGMENT_INDEX = 1;
const BASE64_BLOCK_LENGTH = 4;
const BASE64_PADDING = '=';
const PERMISSION_SCOPE_TYPES = ['system', 'organization', 'parking'];
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isPermissionScopeType = (value) => typeof value === 'string' && PERMISSION_SCOPE_TYPES.includes(value);
const decodeBase64UrlSegment = (segment) => {
    const base64 = segment.replaceAll('-', '+').replaceAll('_', '/');
    const paddedLength = Math.ceil(base64.length / BASE64_BLOCK_LENGTH) * BASE64_BLOCK_LENGTH;
    const binary = atob(base64.padEnd(paddedLength, BASE64_PADDING));
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
};
/** A system role covers no object; organization and parking roles must name the one they cover. */
const readHeldRole = (claim) => {
    if (!isRecord(claim))
        return undefined;
    const { roleKey, scopeType, scopeId } = claim;
    if (typeof roleKey !== 'string' || roleKey === '' || !isPermissionScopeType(scopeType)) {
        return undefined;
    }
    switch (scopeType) {
        case 'system':
            return scopeId === null || scopeId === undefined ? { roleKey, scopeType, scopeId: undefined } : undefined;
        case 'organization':
        case 'parking':
            return typeof scopeId === 'string' && scopeId !== '' ? { roleKey, scopeType, scopeId } : undefined;
        default: {
            const exhaustiveScopeType = scopeType;
            return exhaustiveScopeType;
        }
    }
};
export const readHeldRolesFromAccessToken = (token) => {
    const segments = token.split(TOKEN_SEGMENT_SEPARATOR);
    const payloadSegment = segments[PAYLOAD_SEGMENT_INDEX];
    if (segments.length !== SIGNED_TOKEN_SEGMENT_COUNT || !payloadSegment)
        return [];
    try {
        const payload = JSON.parse(decodeBase64UrlSegment(payloadSegment));
        if (!isRecord(payload) || !Array.isArray(payload.roles))
            return [];
        return payload.roles
            .map((claim) => readHeldRole(claim))
            .filter((heldRole) => heldRole !== undefined);
    }
    catch {
        return [];
    }
};
